import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ConfigService } from '@app/core/services/config.service';
import { MerakiClientService } from '@app/core/services/meraki-client.service';
import { ProviderService } from '@app/core/services/provider.service';
import { shareReplayDestroyed } from '@app/core/functions/share-replay-destroyed';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { ClinicalEncounterService } from '@app/core/services/clinical-encounter.service';
import { AuthService, EncounterVo, PatientsService, UiStateService } from '@app/core/services';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class GenerateFunctionsService {
  currentTenantId$ = this.authService.currentTenantId$;
  currentPatientId$ = this.uiStateService.currentPatientId$;

  params$ = this.currentPatientId$.pipe(
    withLatestFrom(this.currentTenantId$),
    distinctUntilChanged(),
    // debounceTime(10),
    map(([patientId, tenantId]) => ({ tenantId, patientId })),
    untilDestroyed(this)
  );

  patient$ = this.params$.pipe(
    switchMap(params => this.patientsService.patientDetailsById$(params.tenantId, params.patientId)),
    untilDestroyed(this),
    shareReplayDestroyed(1)
  );
  providerDetails$ = this.providerService.provider$;
  encounterInProgress$: Observable<EncounterVo> = this.params$.pipe(
    switchMap(params => this.clinicalEncounterService.encounterInProgress$(params.tenantId, params.patientId)),
    distinctUntilChanged(_.isEqual),
    untilDestroyed(this),
    shareReplayDestroyed(1)
  );

  showPatientLetterSubject: Subject<boolean> = new Subject<boolean>();
  showPatientLetter$ = this.showPatientLetterSubject.asObservable();
  isPatientLetterLoadingSubject: Subject<boolean> = new Subject<boolean>();
  isPatientLetterLoading$ = this.isPatientLetterLoadingSubject.asObservable();
  isReferralLetterLoadingSubject: Subject<boolean> = new Subject<boolean>();
  isReferralLetterLoading$ = this.isReferralLetterLoadingSubject.asObservable();
  showReferralLetterSubject: Subject<boolean> = new Subject<boolean>();
  showReferralLetter$ = this.showReferralLetterSubject.asObservable();

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private merakiClientService: MerakiClientService,
    private providerService: ProviderService,
    private clinicalEncounterService: ClinicalEncounterService,
    private patientsService: PatientsService,
    private authService: AuthService,
    private uiStateService: UiStateService
  ) {}

  // Patient Letter
  generatePatientLetter(letterReason: string, practiceId: string, patientId: string, currentEncounter: EncounterVo): Observable<any> {
    const cloudFunctionUrl = `${this.configService.config.sandbox.cloudFunctionBaseUrl}patient-letter-ai-generation`;

    return this.http.post(
      cloudFunctionUrl,
      {
        reason: letterReason,
        practiceId: practiceId,
        patientId: patientId,
        type: 'visit-summary',
        currentEncounter: currentEncounter,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  streamPatientLetter(
    letterType: string,
    letterReason: string,
    practiceId: string,
    patientId: string,
    currentEncounter: EncounterVo
  ): Observable<string> {
    const url = this.configService.config.sandbox.clinicalaiLettersGenerationFunctionUrl;
    const currentEncounterTrimmed = this.trimEncounter(currentEncounter);

    const body = JSON.stringify({
      reason: letterReason,
      practiceId: practiceId,
      patientId: patientId,
      type: letterType,
      currentEncounter: currentEncounterTrimmed,
    });

    return this.configService.clinicalConfig$.pipe(
      take(1),
      switchMap(
        ({ lettersApiKey }) =>
          new Observable<string>(observer => {
            fetch(url, {
              method: 'POST',
              headers: { 'x-api-key': lettersApiKey, 'Content-Type': 'application/json' },
              body,
            })
              .then(response => {
                if (!response.ok) {
                  return response.json().then(({ error }) => observer.error(error));
                }
                const reader = response.body!.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                function read() {
                  reader
                    .read()
                    .then(({ done, value }) => {
                      if (done) {
                        if (buffer) observer.next(buffer);
                        observer.complete();
                        return;
                      }
                      // Process each chunk
                      const chunk = decoder.decode(value, { stream: true });
                      // If backend uses "data: ..." lines (SSE-like), split and emit each line
                      buffer += chunk;
                      let lines = buffer.split('\n');
                      buffer = lines.pop() || '';
                      for (const line of lines) {
                        if (line.startsWith('data: ')) {
                          try {
                            const data = JSON.parse(line.replace('data: ', ''));
                            if (data.text) observer.next(data.text);
                          } catch (e) {
                            // ignore invalid JSON lines
                          }
                        }
                      }
                      read();
                    })
                    .catch(err => observer.error(err));
                }
                read();
              })
              .catch(err => observer.error(err));
          })
      )
    );
  }

  trimEncounter(encounter: EncounterVo): Partial<EncounterVo> {
    if (!encounter) {
      return {};
    }

    const unusedParameters = ['Communications', 'Questionnaires', 'SymptomQuestionAnswers', 'SymptomQuestions', 'Symptoms'];
    const trimmedEncounter = Object.entries(encounter).reduce((acc, curr) => {
      const [key, value] = curr;
      if (!value || unusedParameters.includes(key)) {
        return acc;
      } else if (Array.isArray(value)) {
        const cleaned = value.map(item => (typeof item === 'object' ? this.trimEncounter(item) : item));
        return cleaned.length > 0 ? { ...acc, [key]: cleaned } : acc;
      } else if (value instanceof Object) {
        const cleaned = this.trimEncounter(value);
        return Object.keys(cleaned).length > 0 ? { ...acc, [key]: cleaned } : acc;
      } else {
        return { ...acc, [key]: value };
      }
    }, {});

    return trimmedEncounter;
  }

  getShowPatientLetterSubject(): Observable<boolean> {
    return this.showPatientLetter$;
  }

  setShowPatientLetterSubject(showPatientLetter: boolean): void {
    this.showPatientLetterSubject.next(showPatientLetter);
  }

  getIsPatientLetterLoadingSubject(): Observable<boolean> {
    return this.isPatientLetterLoading$;
  }

  setIsPatientLetterLoadingSubject(isPatientLetterLoading: boolean): void {
    this.isPatientLetterLoadingSubject.next(isPatientLetterLoading);
  }

  getCurrentEncounter(): Observable<EncounterVo> {
    return this.encounterInProgress$;
  }

  // Referral Letter
  generateReferralLetter(referralReason: string, practiceId: string, patientId: string, currentEncounter: EncounterVo): Observable<any> {
    const cloudFunctionUrl = `${this.configService.config.sandbox.cloudFunctionBaseUrl}patient-letter-ai-generation`;

    return this.http.post(
      cloudFunctionUrl,
      {
        reason: referralReason,
        practiceId: practiceId,
        patientId: patientId,
        type: 'referral-letter',
        currentEncounter: currentEncounter,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  getIsReferralLetterLoadingSubject(): Observable<boolean> {
    return this.isReferralLetterLoading$;
  }

  setIsReferralLetterLoadingSubject(isReferralLetterLoading: boolean): void {
    this.isReferralLetterLoadingSubject.next(isReferralLetterLoading);
  }

  getShowReferralLetterSubject(): Observable<boolean> {
    return this.showReferralLetter$;
  }

  setShowReferralLetterSubject(showReferralLetter: boolean): void {
    this.showReferralLetterSubject.next(showReferralLetter);
  }

  generatePathologyInsights(userMessage: string, practiceId: string, patientId: string, messageHistory: any[], sessionId: string) {
    // console.log(sessionId);
    // console.log(messageHistory);
    // console.log(userMessage);
    const cloudFunctionUrl = `${this.configService.config.sandbox.cloudFunctionBaseUrl}clinicalai-process-pathology-requests`;

    return this.http.post(
      cloudFunctionUrl,
      {
        chatSessionId: sessionId,
        userMessage: userMessage,
        practiceId: practiceId,
        patientId: patientId,
        messages: messageHistory,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  streamPathologyInsightsResponse(userMessage: string, practiceId: string, patientId: string, messageHistory: any[], sessionId: string) {
    const url = this.configService.config.sandbox.clinicalaiClinicalInsightsFunctionUrl;
    const body = JSON.stringify({
      chatSessionId: sessionId,
      userMessage: userMessage,
      practiceId: practiceId,
      patientId: patientId,
      messages: messageHistory,
    });

    return new Observable<string>(observer => {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      })
        .then(response => {
          const reader = response.body!.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          function read() {
            reader
              .read()
              .then(({ done, value }) => {
                if (done) {
                  if (buffer) observer.next(buffer);
                  observer.complete();
                  return;
                }
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                let lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (const line of lines) {
                  if (line.startsWith('data: ')) {
                    try {
                      const data = JSON.parse(line.replace('data: ', ''));
                      if (data.text) observer.next(data.text);
                    } catch (e) {
                      // ignore invalid JSON lines
                    }
                  }
                }
                read();
              })
              .catch(err => observer.error(err));
          }
          read();
        })
        .catch(err => observer.error(err));
    });
  }

  createSandboxSummary(patientId: string) {
    this.providerDetails$
      .pipe(
        take(1),
        switchMap(providerDetails =>
          this.merakiClientService.createSandboxSummary(
            providerDetails.PracticeNumber,
            providerDetails.PracticeId,
            patientId,
            providerDetails.PracticeTenantId,
            providerDetails.HPCSANumber
          )
        )
      )
      .subscribe();
  }

  updatePathologyMessage(
    message: string,
    pathologyReportId: string,
    practiceTenantId: string,
    practiceBPN: string,
    patientName: string,
    letterContent: string,
    testPath: boolean,
    manualUpdate: boolean
  ) {
    const cloudFunctionUrl = `${this.configService.config.sandbox.cloudFunctionBaseUrl}clinicalai-pathology-message-updates-to-patients`;

    return this.http.post(
      cloudFunctionUrl,
      {
        message: message,
        pathologyReportId: pathologyReportId,
        practiceTenantId: practiceTenantId,
        practiceBPN: practiceBPN,
        letterContent: letterContent,
        patientName: patientName,
        testPath: testPath, // ONLY FOR TESTING
        manualUpdate: manualUpdate,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  sendWhatsAppMessage(
    messageType: string,
    phoneNumber: string,
    BPN: string,
    patientId: string,
    patientUUID: string,
    patientName: string,
    hpcsaNumber: string,
    message: string,
    messageIntent: string,
    doctorName: string,
    practiceId: string,
    practiceTenantId: string,
    pathologyReportId?: string
  ) {
    const cloudFunctionUrl = `${this.configService.config.sandbox.cloudFunctionBaseUrl}send-clinical-pathology-report-whatsapp-message`;

    return this.http.post(
      cloudFunctionUrl,
      {
        messageType: messageType,
        message: message,
        messageIntent: messageIntent,
        phoneNumber: phoneNumber,
        BPN: BPN,
        patientId: patientId,
        patientUUID: patientUUID,
        practiceId: practiceId,
        practiceTenantId: practiceTenantId,
        hpcsaNumber: hpcsaNumber,
        doctorName: doctorName,
        patientName: patientName,
        reportId: pathologyReportId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }

  ngOnDestroy(): void {
    this.showPatientLetterSubject.complete();
    this.isPatientLetterLoadingSubject.complete();
    this.isReferralLetterLoadingSubject.complete();
    this.showReferralLetterSubject.complete();
  }
}
